import React, { useState, useEffect } from 'react';
import { Card, Table, Spin, Row, Col } from 'antd';
import { RiseOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import ApiClient from '../../../methods/api/apiClient';

const COLORS = {
  primary: '#3E549D',
  success: '#27ae60',
  warning: '#f39c12',
  danger: '#e74c3c',
};

const GrowthMetrics = ({ board_id, dateRange, summaryData }) => {
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [costAnalysis, setCostAnalysis] = useState([]);
  const [carrierAnalysis, setCarrierAnalysis] = useState([]);

  useEffect(() => {
    // Use pre-fetched summary data if available
    if (summaryData) {
      if (summaryData.topRoutes) {
        const d = Array.isArray(summaryData.topRoutes.total_amount)
          ? summaryData.topRoutes.total_amount
          : (Array.isArray(summaryData.topRoutes) ? summaryData.topRoutes : []);
        setRoutes(d.slice(0, 8));
      }
      if (summaryData.costAnalysis) {
        const d = Array.isArray(summaryData.costAnalysis) ? summaryData.costAnalysis : [];
        setCostAnalysis(d.slice(0, 5));
      }
      if (summaryData.carrierAnalysis) {
        const d = Array.isArray(summaryData.carrierAnalysis) ? summaryData.carrierAnalysis : [];
        setCarrierAnalysis(d.slice(0, 5));
      }
      setLoading(false);
      return;
    }

    // Fallback: fetch individually if summaryData not provided
    const buildParams = () => {
      const params = {};
      if (board_id) params.board_id = board_id;
      if (dateRange?.startDate) params.startDate = dateRange.startDate;
      if (dateRange?.endDate) params.endDate = dateRange.endDate;
      return params;
    };

    const fetchData = () => {
      setLoading(true);
      const params = buildParams();

      const calls = [
        ApiClient.get('performance/top-performing-routes', params, '', true)
          .then(res => {
            if (res?.success || res?.code === 200) {
              const d = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
              return d.slice(0, 8);
            }
            return [];
          })
          .catch(() => []),
        ApiClient.get('admin/dashboard/lane-frequency/cost-analysis', params, '', true)
          .then(res => {
            if (res?.success || res?.code === 200) {
              const d = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
              return d.slice(0, 5);
            }
            return [];
          })
          .catch(() => []),
        ApiClient.get('admin/dashboard/lane-frequency/carrier-analysis', params, '', true)
          .then(res => {
            if (res?.success || res?.code === 200) {
              const d = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
              return d.slice(0, 5);
            }
            return [];
          })
          .catch(() => []),
      ];

      Promise.all(calls)
        .then(([routeData, costData, carrierData]) => {
          setRoutes(routeData);
          setCostAnalysis(costData);
          setCarrierAnalysis(carrierData);
        })
        .finally(() => setLoading(false));
    };

    fetchData();
  }, [board_id, dateRange, summaryData]);

  const getRouteName = (item) => {
    const origin = item.origin_address || item.origin_city || item.origin || '';
    const dest = item.destination_address || item.destination_city || item.destination || '';
    if (origin && dest) {
      const short = (s) => s.length > 18 ? s.substring(0, 18) + '...' : s;
      return `${short(origin)} - ${short(dest)}`;
    }
    return item.route || item.lane || 'Unknown';
  };

  const routeChartOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
    xAxis: { type: 'value', axisLabel: { fontSize: 10 } },
    yAxis: {
      type: 'category',
      data: routes.map(r => getRouteName(r)).reverse(),
      axisLabel: { fontSize: 10, width: 140, overflow: 'truncate' },
    },
    series: [{
      type: 'bar',
      data: routes.map(r => r.count || r.total_loads || r.totalLoads || 0).reverse(),
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 1, y2: 0,
          colorStops: [
            { offset: 0, color: COLORS.primary },
            { offset: 1, color: '#6B7FD7' },
          ],
        },
        borderRadius: [0, 4, 4, 0],
      },
      barWidth: 16,
    }],
  };

  const costCols = [
    { title: 'Lane', dataIndex: 'origin_address', key: 'lane', ellipsis: true,
      render: (text, record) => <span style={{ fontSize: 12 }}>{(text || record.origin || '').substring(0, 20)}</span> },
    { title: 'Loads', dataIndex: 'count', key: 'count', width: 55, align: 'center',
      render: (val, record) => <span style={{ fontWeight: 600, color: COLORS.primary, fontSize: 12 }}>{val || record.total || 0}</span> },
    { title: 'Avg Cost', dataIndex: 'avg_cost', key: 'avg_cost', width: 80, align: 'right',
      render: (val, record) => <span style={{ fontWeight: 600, color: COLORS.success, fontSize: 12 }}>${Math.round(val || record.avgCost || 0).toLocaleString()}</span> },
  ];

  const hasRoutes = routes.length > 0;

  return (
    <Card
      title={
        <span style={{ fontWeight: 600, fontSize: 15 }}>
          <RiseOutlined style={{ color: COLORS.success, marginRight: 8 }} />
          Routes &amp; Growth
        </span>
      }
      bordered={false}
      style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Spin spinning={loading}>
        <Row gutter={16}>
          <Col xs={24} md={14}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>
              Top Performing Routes
            </div>
            {hasRoutes ? (
              <ReactECharts
                option={routeChartOption}
                style={{ height: 220 }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>
                No route data available
              </div>
            )}
          </Col>
          <Col xs={24} md={10}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>
              Cost Analysis by Lane
            </div>
            <Table
              columns={costCols}
              dataSource={costAnalysis.map((c, i) => ({ ...c, key: i }))}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No cost data' }}
              style={{ fontSize: 12 }}
            />
          </Col>
        </Row>
      </Spin>
    </Card>
  );
};

export default GrowthMetrics;
